import { type NotificationVm, maskNotificationContent } from '@/features/notification'
import { formatDateTime } from '@/shared/lib/format'
import { DeleteOutlined as Trash } from '@/shared/ui/icons'
import { type MouseEvent, type ReactNode, useEffect, useRef } from 'react'
import './NotificationItem.scss'

interface NotificationItemProps {
  item: NotificationVm
  severityTone: { icon: ReactNode; tone: string }
  /** Nút xoá đang trượt ra (do list giữ id duy nhất) */
  revealed: boolean
  onRevealChange: (id: string | null) => void
  onClick: (item: NotificationVm) => void
  onDelete: (id: string) => void
}

const NotificationItem = ({
  item,
  severityTone,
  revealed,
  onRevealChange,
  onClick,
  onDelete,
}: NotificationItemProps) => {
  const rootRef = useRef<HTMLDivElement>(null)

  // Đóng khi nhấn Esc, bấm ra ngoài hoặc cuộn — chỉ lắng nghe lúc đang mở
  useEffect(() => {
    if (!revealed) return
    const close = () => onRevealChange(null)
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    const onPointerDown = (e: globalThis.MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    // capture để bắt cả cuộn của vùng danh sách lồng bên trong
    document.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('scroll', close, true)
    }
  }, [revealed, onRevealChange])

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault()
    if (item.canDelete) onRevealChange(item.id)
  }

  const handleRowClick = () => {
    // Đang mở nút xoá: bấm hàng chỉ đóng nút, không mở thông báo
    if (revealed) {
      onRevealChange(null)
      return
    }
    onClick(item)
  }

  return (
    <div
      ref={rootRef}
      className={`notification-item${revealed ? ' is-revealed' : ''}`}
      onContextMenu={handleContextMenu}
    >
      {item.canDelete && (
        <button
          type="button"
          className="notification-item-delete"
          aria-label="Xoá thông báo"
          title="Xoá thông báo"
          aria-hidden={!revealed}
          tabIndex={revealed ? 0 : -1}
          onClick={() => onDelete(item.id)}
        >
          <Trash size={18} />
        </button>
      )}
      <button
        type="button"
        className={`notification-item-row${item.isRead ? '' : ' is-unread'}`}
        onClick={handleRowClick}
      >
        <span className={`notification-item-icon is-${severityTone.tone}`} aria-hidden="true">
          {severityTone.icon}
        </span>
        <span className="notification-item-main">
          <span className="notification-item-title">{item.title}</span>
          {item.content && (
            <span className="notification-item-content">
              {maskNotificationContent(item.content)}
            </span>
          )}
          <span className="notification-item-time">{formatDateTime(item.createdAt)}</span>
        </span>
        {!item.isRead && (
          <span className="notification-item-dot" role="img" aria-label="Chưa đọc" />
        )}
      </button>
    </div>
  )
}

export default NotificationItem
